import {
  Box,
  Button,
  Dialog,
  DialogActionButton,
  DialogActions,
  DialogCancelButton,
  DialogContent,
  DialogHeader,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  ErrorBoundary,
  FormInput,
  Icon,
  IconButton,
  Select,
  SelectItem,
  Text,
  Tooltip
} from "@prismicio/editor-ui"
import { type ReactNode, Suspense, useState } from "react"

import { Flag } from "../../app/components/Locales/Flag"
import { useRepository } from "../../app/services/repository"
import { useRepositoryReleases } from "../../document/documentRouting"
import { getBulkActionsReleaseSize } from "./bulkActionsService"
import { useBulkActionsButton } from "./useBulkActionsButton"

export function CopyDocumentsToLocale() {
  return (
    <ErrorBoundary renderError={Fallback}>
      <Suspense fallback={<Fallback />}>
        <CopyButton />
      </Suspense>
    </ErrorBoundary>
  )
}

function CopyButton() {
  const [open, setOpen] = useState(false)
  const { tooltip, disabled } = useBulkActionsButton()

  return (
    <>
      <Tooltip content={tooltip || "Copy to another locale"}>
        <IconButton
          icon="language"
          onClick={() => setOpen(true)}
          disabled={disabled}
          color="grey"
        />
      </Tooltip>
      <CopyToLocaleDialog open={open} onOpenChange={setOpen} />
    </>
  )
}

interface CopyToLocalFormState {
  /**
   * full -> "Copy all content"
   * light -> "Copy slice structure and assets"
   */
  mode: "full" | "light"
  locales: string[]
  releaseId: string | undefined
  releaseName: string | undefined
}

interface CopyToLocaleDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

function CopyToLocaleDialog(props: CopyToLocaleDialogProps) {
  const { open, onOpenChange } = props
  const [formState, setFormState] = useState(defaultFormState)
  const [isCheckingReleaseSize, setIsCheckingReleaseSize] = useState(false)
  const [requestExceedsLimit, setRequestExceedsLimit] = useState(false)

  const { action, getSelectedDocuments } = useBulkActionsButton()

  function onOpenChangeAndCleanStates(open: boolean) {
    onOpenChange(open)
    setIsCheckingReleaseSize(false)
    setRequestExceedsLimit(false)
    !open && setFormState(defaultFormState)
  }

  function onCopyContent() {
    // don't pass release ID if the selected release ID equals our stable
    // create new release option
    const releaseId = isNewRelease(formState.releaseId)
      ? undefined
      : formState.releaseId

    // map all selected locales onto each selected document
    // each selected locale must be represented by one item per document
    const items = formState.locales
      .map((selectedLocale) =>
        getSelectedDocuments().map((document) => ({
          documentGroupLangId: document.group_lang_id,
          fromLanguageId: document.language.id,
          toLanguageId: selectedLocale,
          status: document.versions[0]?.status ?? "",
          mode: formState.mode
        }))
      )
      .flat()

    action("copyToAnotherLocale", {
      releaseId,
      items,
      releaseName: formState.releaseName
    })

    onOpenChangeAndCleanStates(false)
  }

  function onModeSelect(newMode: string) {
    if (!isAction(newMode)) return

    setFormState({
      ...formState,
      mode: newMode
    })
  }

  /**
   * Validate that the release size does not exceed the limit after a copy action
   * We need to validate before accepting the copy action to avoid errors
   * @returns boolean
   */
  async function validateReleaseSizeAfterCopy(
    releaseId: string | undefined,
    locales: string[]
  ) {
    if (releaseId === undefined || isNewRelease(releaseId)) {
      setRequestExceedsLimit(false)
      return
    }

    setIsCheckingReleaseSize(true)

    const isLimitReached = await isReleaseSizeLimitReached({
      releaseId,
      numberOfLocales: locales.length,
      numberOfDocuments: getSelectedDocuments().length
    })

    setRequestExceedsLimit(isLimitReached)
    setIsCheckingReleaseSize(false)
  }

  async function onLocaleSelect(newLocale: string) {
    const localeIsSelected = formState.locales.includes(newLocale)
    const locales = localeIsSelected
      ? formState.locales.filter((locale) => newLocale !== locale)
      : [...formState.locales, newLocale]

    setFormState({
      ...formState,
      locales
    })

    // must be validate after setting the state to not block state update
    // we want to validate the release size if the user changes their locales
    // as each locale adds the number of locales * documents on top of a release size
    await validateReleaseSizeAfterCopy(formState.releaseId, locales)
  }

  async function onReleaseSelect(newReleaseId: string) {
    const releaseName = isNewRelease(newReleaseId) ? "" : undefined

    const releaseIsSelected = formState.releaseId === newReleaseId
    const releaseId = releaseIsSelected ? undefined : newReleaseId

    setFormState({
      ...formState,
      releaseId,
      releaseName
    })

    // must be validate after setting the state to not block state update
    // release can be selected even though it exceeds the limit but the action
    // won't be available until a validate release size is achieved
    await validateReleaseSizeAfterCopy(releaseId, formState.locales)
  }

  function onReleaseNameChange(newReleaseName: string) {
    setFormState({
      ...formState,
      releaseName: newReleaseName
    })
  }

  const noLocalesSelected = formState.locales.length === 0
  const noReleaseSelected = formState.releaseId === undefined
  const noNewReleaseName =
    isNewRelease(formState.releaseId) && !formState.releaseName?.trim()

  const disabled =
    isCheckingReleaseSize ||
    noLocalesSelected ||
    noReleaseSelected ||
    requestExceedsLimit ||
    noNewReleaseName

  function getTooltip() {
    if (noLocalesSelected) {
      return "No locales selected"
    }

    if (noReleaseSelected) {
      return "No release selected"
    }

    if (requestExceedsLimit) {
      return "Releases are limited to 200 documents"
    }

    if (noNewReleaseName) {
      return "You must give the new release a name"
    }

    return ""
  }

  return (
    <Dialog
      onOpenChange={onOpenChangeAndCleanStates}
      open={open}
      size="small"
      modal
    >
      <DialogHeader icon="language" title="Copy to another locale" />

      <DialogContent>
        <Box
          flexDirection="column"
          padding={{ inline: 16, top: 16, bottom: 4 }}
          flexGrow={1}
          gap={16}
        >
          <ModeSelect value={formState.mode} onChange={onModeSelect} />
          <LocaleSelect value={formState.locales} onSelect={onLocaleSelect} />
          <ReleaseSelect
            value={formState.releaseId}
            onSelect={onReleaseSelect}
            error={
              requestExceedsLimit
                ? "Releases are limited to 200 documents. Select fewer documents or fewer locales."
                : ""
            }
          />

          {isNewRelease(formState.releaseId) && (
            <FormInput
              label="New release name"
              value={formState.releaseName ?? ""}
              onValueChange={onReleaseNameChange}
            />
          )}
        </Box>

        <DialogActions>
          <DialogCancelButton>Cancel</DialogCancelButton>
          <DialogActionButton
            color="purple"
            disabled={disabled}
            tooltipLabel={getTooltip()}
            onClick={onCopyContent}
            loading={isCheckingReleaseSize}
          >
            Copy content
          </DialogActionButton>
        </DialogActions>
      </DialogContent>
    </Dialog>
  )
}

function Fallback() {
  return <IconButton icon="language" disabled color="grey" />
}

interface LabelProps {
  label: string
  children: ReactNode
}

function Label(props: LabelProps) {
  const { label, children } = props

  return (
    <Box flexDirection="column" gap={4}>
      <Text variant="emphasized">{label}</Text>
      {children}
    </Box>
  )
}

interface ModeSelectProps {
  value: string
  onChange: (newValue: string) => void
}

function ModeSelect(props: ModeSelectProps) {
  const { onChange, value } = props

  return (
    <Label label="Select copy mode">
      <Select
        size="large"
        color="grey"
        value={value}
        onValueChange={onChange}
        flexContent
        side="bottom"
        sideOffset={4}
        constrainContentWidth
      >
        {modes.map((mode) => (
          <SelectItem key={mode} value={mode}>
            {readableMode[mode]}
          </SelectItem>
        ))}
      </Select>
    </Label>
  )
}

interface LocaleSelectProps {
  value: string[]
  onSelect: (selectedValue: string) => void
}

function LocaleSelect(props: LocaleSelectProps) {
  const { value, onSelect } = props
  const { languages: locales } = useRepository()

  return (
    <DropdownMenu modal>
      <Label label="Locales to copy the content to">
        <DropdownMenuTrigger>
          <Button
            endIcon="arrowDropDown"
            size="large"
            color="grey"
            flexContent
            textWeight="normal"
            renderStartIcon={() =>
              value.length === 1 && value[0] && <Flag locale={value[0]} />
            }
          >
            {value.length === 0 && "Select one or multiple locales"}
            {value.length === 1 &&
              locales.find((locale) => locale.id === value[0])?.name}
            {value.length > 1 && `${value.length} locales selected`}
          </Button>
        </DropdownMenuTrigger>
      </Label>

      <DropdownMenuContent
        align="start"
        maxHeight={256}
        minWidth="full-trigger-width"
      >
        {locales.map((locale) => (
          <DropdownMenuItem
            key={locale.id}
            startIcon={<Flag locale={locale.id} />}
            endIcon={value.includes(locale.id) && <Icon name="check" />}
            onSelect={() => onSelect(locale.id)}
          >
            {locale.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

interface ReleaseSelectProps {
  value: string | undefined
  onSelect: (newValue: string) => void
  error: string
}

function ReleaseSelect(props: ReleaseSelectProps) {
  const { onSelect, value, error } = props

  const isCreateNewReleaseOption = value === createNewReleaseValue
  const releases = useRepositoryReleases()
  const releaseName = isCreateNewReleaseOption
    ? "Create a new release"
    : releases.find((release) => release.id === value)?.label

  return (
    <Box flexDirection="column" gap={4}>
      <DropdownMenu modal>
        <Label label="Release to copy your documents to">
          <DropdownMenuTrigger>
            <Button
              endIcon="arrowDropDown"
              size="large"
              color="grey"
              flexContent
              textWeight="normal"
            >
              {releaseName ?? "Select a release or create one"}
            </Button>
          </DropdownMenuTrigger>
        </Label>

        <DropdownMenuContent
          align="start"
          maxHeight={256}
          minWidth="full-trigger-width"
        >
          {releases.map((release) => (
            <DropdownMenuItem
              key={release.id}
              endIcon={value === release.id && <Icon name="check" />}
              onSelect={() => onSelect(release.id)}
            >
              {release.label}
            </DropdownMenuItem>
          ))}
          <DropdownMenuItem
            endIcon={value === createNewReleaseValue && <Icon name="check" />}
            onSelect={() => onSelect(createNewReleaseValue)}
          >
            Create a new release
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {error && (
        <Box alignItems="center" gap={4}>
          <Icon name="alert" size="small" color="tomato11" />
          <Text color="tomato11">{error}</Text>
        </Box>
      )}
    </Box>
  )
}
const createNewReleaseValue = "__PRISMIC_CREATE_NEW_RELEASE__"
const modes = ["full", "light"] as const
const readableMode: Record<CopyToLocalFormState["mode"], string> = {
  full: "Copy all content",
  light: "Copy slice structure and assets"
}
const defaultFormState: CopyToLocalFormState = {
  mode: modes[0],
  locales: [],
  releaseId: undefined,
  releaseName: undefined
}

function isAction(mode: string): mode is CopyToLocalFormState["mode"] {
  return modes.includes(mode as CopyToLocalFormState["mode"])
}

function isNewRelease(releaseId: string | undefined) {
  return releaseId === createNewReleaseValue
}

interface IsReleaseSizeLimitReachedArgs {
  numberOfDocuments: number
  numberOfLocales: number
  releaseId: string
}

export async function isReleaseSizeLimitReached(
  args: IsReleaseSizeLimitReachedArgs
) {
  const { releaseId, numberOfDocuments, numberOfLocales } = args
  const { size: releaseSize } = await getBulkActionsReleaseSize(releaseId)

  return numberOfDocuments * numberOfLocales + releaseSize > 200
}
